import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {NgForm} from '@angular/forms';
import {GaoUserResponse, User} from "../../models/user";
import {CaseStatus, ProtestInfo} from "../../models/protest";
import {HttpService} from "../../services/http.service";
import {StateService} from "../../services/state.service";
import {HttpHeaders} from "@angular/common/http";
import {catchError, Subject, Subscription, takeUntil, throwError} from "rxjs";
import { debounceTime } from 'rxjs/operators';
import {UtilService} from "../../services/util.service";
import {MatTable, MatTableDataSource} from "@angular/material/table";
import {DatePipe, formatDate} from '@angular/common';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from "@angular/material/sort";
import {AttorneyInfoResponse} from "../../models/docket";
import {SidebarService} from "../../services/sidebar.service";
import {Agencies} from "../../models/agencies";
import {AgencySelectionComponent} from "../agency-selection/agency-selection.component";
import {CaseDocketService} from "../../services/case-docket.service";

export interface AdvancedSearchResponse {
  protestInfoList: ProtestInfo[],
  advanceSearchDTO: advanceSearchData,
  response: string
}

export interface advanceSearchData{
  a_No: string,
  b_No: string,
  case_Status: CaseStatus,
  case_Type: string,
  company_Name: string,
  partyInfo: string,
  intervenorCompName: string,
  startSubmission_Date: Date,
  endSubmission_Date: Date,
  startDue_Date: Date,
  endDue_Date: Date,
  solicitation_No: string,
  lawGroup: string,
  attorneyId: string,
  tier1AgencyId: string,
  tier2AgencyId: string,
  listOfAgencyInfoIds: string[],
  partyUserIds: string[],
  onlyPrimaryANos: string[],
}

@Component({
    selector: 'app-advanced-search',
    templateUrl: './advanced-search.component.html',
    styleUrls: ['./advanced-search.component.scss'],
    standalone: false
})

export class AdvancedSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  protected ngUnsubscribe = new Subject<void>();

  displayedColumns: string[] = ['b_No','company_Name', 'submission_Date', 'agency_Name', 'solicitation_No', 'due_Date',
    'attorney_Group_Id', 'attorney_Name', 'case_Type', 'case_Status'];
  dataSource = new MatTableDataSource<ProtestInfo>();
  searching = false;
  disableSearch = false;
  a_No!: string;
  caseStatuses = [ {
    value : 'OPEN',
    text : 'OPEN'
  }, {
    value : 'CLOSED',
    text : 'CLOSED'
  },{
    value : '60DAYS',
    text : 'PUBLIC DECISION +60 DAYS'
  },{
    value : 'RTC',
    text : 'READY TO COMPLETE'
  },{
    value : 'COMPLETE',
    text : 'COMPLETE'
  },];

  caseTypes = [ {
    value : 'PROTEST',
    text : 'PROTEST'
  }, {
    value : 'RECONSIDERATION',
    text : 'RECONSIDERATION'
  }, {
    value : 'ENTITLEMENT',
    text : 'ENTITLEMENT'
  }, {
    value : 'COST-CLAIM',
    text : 'COST-CLAIM'
  }, ];

  groupNoList!: string[];
  attorneyList!: User[];
  audit: boolean = false;
  defaultSortColumn = "b_No";

  selectedAgencies!: Agencies;

  @ViewChild(MatTable) table!: MatTable<AdvancedSearchResponse>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('f', { read: NgForm }) form!: NgForm;
  @ViewChild('agencies') agencyComponent!: AgencySelectionComponent;

  exportUrl = "/epds/advanceSearch-export-to-excel";
  docketSub!: Subscription;

  hideSearchFilters = false;
  showSubDateError = false;
  showDueDateError = false;

  constructor(public util: UtilService,
              private httpService: HttpService,
              public stateService: StateService,
              private activatedRoute: ActivatedRoute,
              private sidebarService : SidebarService,
              private caseDocketService : CaseDocketService,
              private datePipe : DatePipe) { }

  ngOnInit(): void {
    this.a_No = this.activatedRoute.snapshot.params['a_No'];
    this.audit = this.activatedRoute.snapshot.data['audit'];
    if (this.audit) {
      this.displayedColumns = ['a_No','b_No', 'case_Type', 'pay_dot_gov_id', 'submission_Date', 'transaction_Amount'];
      this.defaultSortColumn = "a_No";
      this.exportUrl = "/epds/export-to-excel";
    }

    this.loadAttorneyList();

    if (this.a_No) {
      this.caseDocketService.loadDocket(this.a_No);
      this.docketSub = this.caseDocketService.docketSub$.subscribe((response: AttorneyInfoResponse|undefined) => {
        if (!response) return;
        this.sidebarService.setNavByUser(this.stateService.user.role_id, response.protestInfo);
      });
    }
  }

  ngOnDestroy(): void {
    if (this.a_No) {
      this.docketSub.unsubscribe();
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = this.util.sortingDataAccessor;

    this.form.valueChanges!.pipe(
      debounceTime(250)
    ).subscribe(data => this.checkDisableSearch(data));
  }

  private checkDisableSearch(data: any) {
    // console.log(data);
    let count = 0;
    Object.keys(data).forEach(key => data[key] && data[key].toString().length>0 && count++);
    if (this.selectedAgencies?.tier1Agency?.agency_Id) count++;
    this.disableSearch = count == 0;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value ?? '';
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  searchProtestInfo(params: any) {
    this.searching = true;
    if (params.startSubmission_Date != null) {
      params.startSubmission_Date = formatDate(params.startSubmission_Date, 'MM/dd/yyyy', 'en-US');
    }
    if(params.endSubmission_Date != null) {
      params.endSubmission_Date = formatDate(params.endSubmission_Date, 'MM/dd/yyyy', 'en-US');
    }
    if(params.startDue_Date != null) {
      params.startDue_Date = formatDate(params.startDue_Date, 'MM/dd/yyyy', 'en-US');
    }
    if ( params.endDue_Date!= null) {
      params.endDue_Date = formatDate(params.endDue_Date, 'MM/dd/yyyy', 'en-US');
    }

    params.onlyPrimaryANos = this.audit;

    if (this.selectedAgencies?.tier1Agency?.agency_Id)
      params.tier1AgencyId = this.selectedAgencies.tier1Agency.agency_Id;
    if (this.selectedAgencies?.tier2Agency?.agency_Id)
      params.tier2AgencyId = this.selectedAgencies.tier2Agency.agency_Id;


    const form = new FormData();
    Object.keys(params).forEach(key => [undefined, null, ''].includes(params[key]) && delete params[key]);
    Object.keys(params).forEach(key => form.append(key, params[key]));
    console.log(params);

    this.httpService.Post<AdvancedSearchResponse>('/advance-search', form).pipe(
      takeUntil(this.ngUnsubscribe),
      catchError(err => {
        this.searching = false;
        return throwError(err);
      })
    ).subscribe((response) => {
      // console.log(response);

      if (response.protestInfoList) {
        // For Audit, add transaction amount for sorting
        // if (this.audit) response.protestInfoList.forEach(protest => {
        //   if (protest.pay_dot_gov_id != null) {
        //     protest.transaction_Amount = '$350';
        //   }
        // });
        this.dataSource.data = response.protestInfoList;
        this.hideSearchFilters = response.protestInfoList.length > 0;
      } else {
        this.dataSource.data = [];
        // this.util.displayDialogMessage("No Results", "No results found.");
      }
      this.searching = false;
    });
  }

  private loadAttorneyList() {
    const headers = new HttpHeaders({'Content-Type': 'application/json'});
    const options = {...headers};
    this.httpService.Get<GaoUserResponse>('/get-attorney-list', options).pipe(
      catchError(err => {
        return throwError(err);
      })
    ).subscribe((response) => {
      // console.log(response);
      this.attorneyList = response.gao_User_Info_List;
      this.groupNoList = [];
      for (const attorney of this.attorneyList) {
        if (!this.groupNoList.includes(attorney.group_No)) {
          this.groupNoList.push(attorney.group_No);
        }
      }
      this.groupNoList.sort();
      this.attorneyList.sort(this.sortName);
    });
  }

  sortName(n1: User, n2: User) {
    const n1LN = n1.last_Name.toLowerCase();
    const n2LN = n2.last_Name.toLowerCase();
    if (n1LN > n2LN) {
      return 1;
    } else if (n1LN < n2LN) {
      return -1;
    }

    const n1FN = n1.first_Name.toLowerCase();
    const n2FN = n2.first_Name.toLowerCase();

    if (n1FN > n2FN) {
      return 1;
    } else if (n1FN < n2FN) {
      return -1;
    }

    return 0;
  }

  onExport() {
    window.open(this.util.getBaseURL() + this.exportUrl, '_blank');
  }



  onSelectedAgencies(event: Agencies) {
    console.log(event);
    this.selectedAgencies = event;
    this.checkDisableSearch(this.form.value);
  }

  onReset() {
    if (!this.audit) this.agencyComponent.onReset();
    this.hideSearchFilters = false;
    this.showSubDateError = false;
    this.showDueDateError = false;
  }

  onToggleSearchFilters() {
    this.hideSearchFilters = !this.hideSearchFilters;
  }

  isDateFromBeforeTo(dateFrom: string, dateTo: string) {
    if (!dateFrom || !dateTo) return false;
    return (this.datePipe.transform(dateFrom, 'yyMMdd')||'') >= (this.datePipe.transform(dateTo, 'yyMMdd')||'');
  }

  onSubDateChange(event: any) {
    this.showSubDateError = this.isDateFromBeforeTo(this.stateService.adv_search_submissionFromDate, this.stateService.adv_search_submissionToDate);
  }

  onDueDateChange(event: any) {
    this.showDueDateError = this.isDateFromBeforeTo(this.stateService.adv_search_dueFromDate, this.stateService.adv_search_dueToDate);
  }
}
